import { useEffect, useState, useRef } from 'react';

/**
 * Options for useFormDirty hook
 */
interface UseFormDirtyOptions<T extends Record<string, unknown>> {
  /** Initial values of the form fields */
  initialValues: T;
  /** Current values of the form fields */
  currentValues: T;
  /** Whether to enable dirty tracking (default: true) */
  enableTracking?: boolean;
}

/**
 * Hook to track if a form has unsaved changes (is "dirty")
 * Compares current form values against initial values using shallow comparison.
 *
 * ## When to use this hook vs a pristine flag
 *
 * Use **`useFormDirty`** ONLY for modals/pages whose form state is:
 *   - Initialized entirely via `useState(initial)` (synchronous), AND
 *   - Not modified by any `useEffect(() => setFormData(...))` after mount
 *
 * Use a **`pristine` flag** (state that flips to `false` only inside user input
 * handlers) for any modal/page that has a prefill `useEffect` writing to form
 * state — e.g. `useEffect(() => setFormData(prefilled), [isOpen, props])`.
 *
 * ### Why
 *
 * `useFormDirty` stores `initialValues` in a ref that is updated by a separate
 * `useEffect([initialValues])`. When both `initialValues` and `currentValues`
 * change in the same React tick (the prefill-on-open case), the dirty-check
 * effect can run before the ref-update effect has observed the new
 * `initialValues`, reporting a false positive dirty state. Recreating the
 * baseline manually via captured state (`baselineFormData`) pushes the race
 * onto the consumer and doesn't fix it reliably.
 *
 * The `pristine` flag sidesteps the race entirely: it only flips on explicit
 * user input, so auto-fill effects don't move it.
 *
 * ### Reference
 *
 * Rule documented in `docs/quality-hardening-pass/credibility-close-out.md`
 * and applied during the Phase 2 modal CTM sweep (see commits tagged
 * `fix(modal): … confirm before discarding edits`).
 *
 * @example
 * ```tsx
 * // Safe — no prefill effect, state initialized synchronously.
 * const [name, setName] = useState('John');
 * const isDirty = useFormDirty({
 *   initialValues: { name: 'John' },
 *   currentValues: { name }
 * });
 * ```
 *
 * @example
 * ```tsx
 * // Unsafe — prefill effect writes to formData on open. Use `pristine` instead.
 * const [formData, setFormData] = useState(blankState);
 * const [pristine, setPristine] = useState(true);
 * useEffect(() => { if (isOpen && entity) setFormData(entity); }, [isOpen, entity]);
 * const handleChange = (v) => { setPristine(false); setFormData(v); };
 * const isDirty = !pristine;
 * ```
 */
export const useFormDirty = <T extends Record<string, unknown>>(options: UseFormDirtyOptions<T>): boolean => {
  const { initialValues, currentValues, enableTracking = true } = options;
  const [isDirty, setIsDirty] = useState(false);
  
  // Store initial values in a ref to avoid re-running effect unnecessarily
  const initialValuesRef = useRef(initialValues);
  
  // Update the ref when initialValues changes (important for capturing state after auto-population)
  useEffect(() => {
    initialValuesRef.current = initialValues;
  }, [initialValues]);

  useEffect(() => {
    if (!enableTracking) {
      setIsDirty(false);
      return;
    }

    // Compare current values with initial values
    const hasChanges = Object.keys(currentValues).some(key => {
      const currentValue = currentValues[key];
      const initialValue = initialValuesRef.current[key];

      // Handle null/undefined comparison
      if (currentValue === initialValue) {
        return false;
      }

      // Handle null vs undefined as equivalent
      if ((currentValue === null || currentValue === undefined) && 
          (initialValue === null || initialValue === undefined)) {
        return false;
      }

      // Handle array comparison
      if (Array.isArray(currentValue) && Array.isArray(initialValue)) {
        if (currentValue.length !== initialValue.length) {
          return true;
        }
        return currentValue.some((val, idx) => val !== initialValue[idx]);
      }

      // Handle object comparison (shallow)
      if (typeof currentValue === 'object' && currentValue !== null &&
          typeof initialValue === 'object' && initialValue !== null) {
        const currentObj = currentValue as Record<string, unknown>;
        const initialObj = initialValue as Record<string, unknown>;
        const currentKeys = Object.keys(currentObj);
        const initialKeys = Object.keys(initialObj);
        
        if (currentKeys.length !== initialKeys.length) {
          return true;
        }
        
        return currentKeys.some(objKey => currentObj[objKey] !== initialObj[objKey]);
      }

      // Simple value comparison
      return currentValue !== initialValue;
    });

    setIsDirty(hasChanges);
  }, [currentValues, enableTracking]);

  return isDirty;
};

/**
 * Hook variant for react-hook-form
 * Simply returns the formState.isDirty value
 * 
 * @example
 * ```tsx
 * const { formState } = useForm();
 * const isDirty = useFormDirtyRHF(formState.isDirty);
 * ```
 */
export const useFormDirtyRHF = (isDirty: boolean): boolean => {
  return isDirty;
};